import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import ExcelJS from 'exceljs';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import api from '../api/axiosConfig';
import { apiErrorText } from '../utils/apiErrors';
import ExportPreviewModal from '../components/ExportPreviewModal';
import { hasNavPermission } from '../components/ProtectedRoute';
import RowProperties from '../components/RowProperties';
import { useAuth } from '../context/AuthContext';
import { formatISODate, isoWeekYear, parseISODate } from '../utils/dates';
import { fmtDoseUnit, fmtVolume, fumigationMessage } from '../utils/fumigation';
import './CompaniesPage.css';
import './SalesFormPage.css';
import '../styles/topbarButtons.css';
import '../styles/rowActions.css';
import '../styles/productionSummary.css';
import '../styles/fumigation.css';
import '../styles/pageHeader.css';

// Historial de Fumigación (Producción): the fumigations of a year for one farm or all of them
// ("Todas las fincas"), filtered by Empresa / Finca / Año / Semana, with ℹ️ / ✏️ (opens the form) / 🗑️ on
// each row, plus 💬 for that fumigation's message alone. "Exportar" offers two formats: Tabla previews the table like the farms' sheet (one column
// per product) and downloads it as Excel, PDF or JPEG; Mensaje writes each fumigation like the text
// sent by WhatsApp (utils/fumigation.js fumigationMessage), to copy or download as .txt.
// Data: fumigations/ (core/views.py FumigationApplicationViewSet).

const FARM_KEY = 'farmershub_fumigation_history_farm';
const COMPANY_KEY = 'farmershub_fumigation_company';
const ALL_FARMS = 'all';

// ---- export: the sheet's layout, one column per product of the row ----
const GREEN = '2F7D32';
const LIGHT_GREEN = 'C8E6C9';
const AFTER_PRODUCTS = [
  'FRECUENCIA', 'SEMANA', 'FECHA', 'TIPO APLICACIÓN', 'VOLUMEN', 'HA FUMIGADAS', 'COMPAÑÍA', 'PILOTO', 'OBSERVACIONES',
];
const fmtHa = (v) => (v == null ? '' : Number(v).toLocaleString('es-EC', { minimumFractionDigits: 2, maximumFractionDigits: 2 }));

// `withFarm` adds a FINCA column first (when several farms are listed).
function sheetModel(rows, withFarm) {
  const productCols = Math.max(1, ...rows.map((r) => r.products.length));
  const lead = withFarm ? ['FINCA', 'CICLO'] : ['CICLO'];
  const body = rows.map((r) => [
    ...(withFarm ? [r.farm_name] : []),
    r.cycle,
    ...Array.from({ length: productCols }, (_, i) => (r.products[i] ? `${r.products[i].product_name} ${fmtDoseUnit(r.products[i])}` : '')),
    r.frequency == null ? '' : r.frequency,
    r.week,
    formatISODate(r.date),
    r.application_type_display.toUpperCase(),
    fmtVolume(r.volume),
    fmtHa(r.fumigated_hectares),
    r.fumigation_company_name,
    r.pilot_name,
    r.observations || '',
  ]);
  return { lead, productCols, body, width: lead.length + productCols + AFTER_PRODUCTS.length };
}

async function exportExcel(title, subtitle, fileName, rows, withFarm) {
  const { lead, productCols, body, width } = sheetModel(rows, withFarm);
  const firstProduct = lead.length + 1; // 1-based column of the first product
  const workbook = new ExcelJS.Workbook();
  const sheet = workbook.addWorksheet('Fumigación', { views: [{ state: 'frozen', ySplit: 4 }] });
  const thin = { style: 'thin', color: { argb: 'FF000000' } };
  const border = { top: thin, left: thin, bottom: thin, right: thin };
  const solid = (hex) => ({ type: 'pattern', pattern: 'solid', fgColor: { argb: `FF${hex}` } });

  sheet.mergeCells(1, 1, 1, width);
  const t = sheet.getCell(1, 1);
  t.value = title;
  t.fill = solid(GREEN);
  t.font = { bold: true, size: 13, color: { argb: 'FFFFFFFF' } };
  t.alignment = { horizontal: 'center' };
  sheet.mergeCells(2, 1, 2, width);
  sheet.getCell(2, 1).value = subtitle;
  sheet.getCell(2, 1).font = { bold: true };
  sheet.getCell(2, 1).alignment = { horizontal: 'center' };
  sheet.addRow([]);
  const head = sheet.addRow([...lead, 'PRODUCTOS', ...Array(productCols - 1).fill(''), ...AFTER_PRODUCTS]);
  if (productCols > 1) sheet.mergeCells(head.number, firstProduct, head.number, firstProduct + productCols - 1);
  head.eachCell({ includeEmpty: true }, (c, col) => {
    if (col > width) return;
    c.fill = solid(LIGHT_GREEN);
    c.border = border;
    c.font = { bold: true, size: 9 };
    c.alignment = { horizontal: 'center', vertical: 'middle', wrapText: true };
  });
  body.forEach((cells) => {
    const row = sheet.addRow(cells);
    row.eachCell({ includeEmpty: true }, (c, col) => {
      if (col > width) return;
      const isProduct = col >= firstProduct && col < firstProduct + productCols;
      c.border = border;
      c.font = { size: 9, bold: col === lead.length };
      c.alignment = { horizontal: isProduct || (withFarm && col === 1) ? 'left' : 'center', vertical: 'middle', wrapText: true };
    });
  });
  if (withFarm) sheet.getColumn(1).width = 18;
  sheet.getColumn(lead.length).width = 7;
  for (let i = 0; i < productCols; i += 1) sheet.getColumn(firstProduct + i).width = 20;
  [11, 9, 12, 16, 10, 13, 18, 22, 28].forEach((w, i) => { sheet.getColumn(firstProduct + productCols + i).width = w; });

  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `${fileName}.xlsx`;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

function exportPDF(title, subtitle, fileName, rows, withFarm) {
  const { lead, productCols, body, width } = sheetModel(rows, withFarm);
  const doc = new jsPDF({ orientation: 'landscape', format: productCols > 5 || withFarm ? 'a3' : 'a4' });
  autoTable(doc, {
    startY: 12,
    theme: 'grid',
    head: [
      [{ content: title, colSpan: width, styles: { fillColor: `#${GREEN}`, textColor: '#ffffff', halign: 'center', fontSize: 11 } }],
      [{ content: subtitle, colSpan: width, styles: { fillColor: '#ffffff', halign: 'center', fontStyle: 'bold' } }],
      [...lead, { content: 'PRODUCTOS', colSpan: productCols }, ...AFTER_PRODUCTS],
    ],
    body: body.map((cells) => cells.map((v) => String(v))),
    styles: { fontSize: 7, cellPadding: 1, halign: 'center', valign: 'middle', lineColor: [0, 0, 0], lineWidth: 0.1, textColor: [0, 0, 0] },
    headStyles: { fillColor: `#${LIGHT_GREEN}`, textColor: [0, 0, 0], fontStyle: 'bold' },
    columnStyles: Object.fromEntries([
      ...(withFarm ? [[0, { halign: 'left' }]] : []),
      ...Array.from({ length: productCols }, (_, i) => [lead.length + i, { halign: 'left' }]),
    ]),
    didParseCell: (cell) => {
      if (cell.section === 'body' && cell.column.index === lead.length - 1) cell.cell.styles.fontStyle = 'bold';
    },
  });
  doc.save(`${fileName}.pdf`);
}

// The Fecha column's hover note: the full date, the week's Monday–Sunday and the frequency with
// the previous fumigation's date.
const longDate = (d) => d.toLocaleDateString('es-EC', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
const shortDate = (d) => d.toLocaleDateString('es-EC', { day: 'numeric', month: 'short' }).replace('.', '');
const addDays = (d, n) => new Date(d.getFullYear(), d.getMonth(), d.getDate() + n);

// "Mensaje" export: each fumigation as its WhatsApp-style text, with Copiar per message, Copiar todo
// and a .txt download.
function MessageExport({ rows, title, fileName, onClose }) {
  const [copied, setCopied] = useState(null);  // { key: row id or 'all', ok }
  const messages = rows.map((r) => ({ id: r.id, text: fumigationMessage(r) }));
  const allText = messages.map((m) => m.text).join('\n\n');

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  // The clipboard API, or a hidden textarea + execCommand where the browser blocks it.
  const copyText = (text) => (navigator.clipboard?.writeText(text) || Promise.reject())
    .catch(() => {
      const area = document.createElement('textarea');
      area.value = text;
      area.setAttribute('readonly', '');
      area.style.position = 'fixed';
      area.style.opacity = '0';
      document.body.appendChild(area);
      area.select();
      const ok = document.execCommand('copy');
      area.remove();
      if (!ok) throw new Error('copy failed');
    });
  const copy = (key, text) => {
    copyText(text)
      .then(() => setCopied({ key, ok: true }))
      .catch(() => setCopied({ key, ok: false }))
      .finally(() => setTimeout(() => setCopied((c) => (c?.key === key ? null : c)), 2000));
  };
  const copyLabel = (key, label) => {
    if (copied?.key !== key) return label;
    return copied.ok ? '✓ Copiado' : '⚠️ No se pudo copiar';
  };
  const downloadTxt = () => {
    const url = URL.createObjectURL(new Blob([allText], { type: 'text/plain;charset=utf-8' }));
    const link = document.createElement('a');
    link.href = url;
    link.download = `${fileName}.txt`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="export-preview-overlay" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="export-preview-dialog fum-msg-dialog" role="dialog" aria-modal="true" aria-label={`Mensajes: ${title}`}>
        <div className="export-preview-header">
          <div>
            <div className="export-preview-title">💬 Mensajes</div>
            <div className="export-preview-subtitle">{title}</div>
          </div>
          <button type="button" className="export-preview-close" onClick={onClose} aria-label="Cerrar">✖️</button>
        </div>
        <div className="export-preview-body fum-msg-body">
          {messages.map((m) => (
            <div key={m.id} className="fum-msg">
              <pre className="fum-msg-text">{m.text}</pre>
              <button type="button" className="fum-msg-copy" onClick={() => copy(m.id, m.text)}>
                {copyLabel(m.id, '📋 Copiar')}
              </button>
            </div>
          ))}
        </div>
        <div className="export-preview-footer">
          <span className="export-preview-hint">
            {messages.length === 1 ? '1 mensaje, listo' : `${messages.length} mensajes, listos`} para pegar en WhatsApp.
          </span>
          <div className="export-preview-actions">
            <button type="button" className="export-preview-btn" onClick={onClose}>Cerrar</button>
            <button type="button" className="export-preview-btn export-preview-btn-excel" onClick={() => copy('all', allText)}>
              {copyLabel('all', messages.length === 1 ? '📋 Copiar' : '📋 Copiar todo')}
            </button>
            <button type="button" className="export-preview-btn export-preview-btn-jpeg" onClick={downloadTxt}>📄 Descargar .txt</button>
          </div>
        </div>
      </div>
    </div>
  );
}

function DateNote({ row }) {
  const date = parseISODate(row.date);
  const monday = addDays(date, -((date.getDay() + 6) % 7));
  const previous = row.frequency == null ? null : addDays(date, -row.frequency);
  return (
    <span className="fum-date-tip" role="tooltip">
      <span><b>Fecha:</b> {longDate(date)}</span>
      <span><b>Semana:</b> {row.week} ({shortDate(monday)} – {shortDate(addDays(monday, 6))})</span>
      <span>
        <b>Frecuencia:</b>{' '}
        {previous
          ? `${row.frequency} días desde la fumigación anterior (${previous.getDate()}/${previous.getMonth() + 1}/${previous.getFullYear()})`
          : 'primera fumigación del año en esta finca'}
      </span>
    </span>
  );
}

export default function FumigationHistoryPage() {
  const { user } = useAuth();
  const canDelete = hasNavPermission(user, 'delete_records');
  const thisYear = isoWeekYear(new Date());

  const [allFarms, setAllFarms] = useState([]);
  const [companyId, setCompanyId] = useState(() => {
    try { return localStorage.getItem(COMPANY_KEY) || ''; } catch { return ''; }
  }); // '' = Todas las empresas
  const [farmId, setFarmId] = useState(() => {
    try { return localStorage.getItem(FARM_KEY) || ''; } catch { return ''; }
  });
  const [year, setYear] = useState(thisYear);
  const [week, setWeek] = useState('');  // '' = Todas las semanas (only the weeks with fumigations are offered)
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState(null);
  const [preview, setPreview] = useState(false);
  const [messages, setMessages] = useState(null);  // { rows, title, fileName } of the Mensaje window
  const [exportMenu, setExportMenu] = useState(false);

  useEffect(() => {
    api.get('farms/').then((r) => setAllFarms(r.data))
      .catch(() => setStatus({ type: 'error', text: 'No se pudieron cargar las fincas.' }));
  }, []);

  // Companies come from the farms the user can see; the Finca list follows the chosen company.
  const companies = useMemo(() => {
    const byId = new Map(allFarms.map((f) => [String(f.company), f.company_name]));
    return [...byId].map(([id, name]) => ({ id, name })).sort((a, b) => a.name.localeCompare(b.name));
  }, [allFarms]);
  const farms = useMemo(
    () => (companyId ? allFarms.filter((f) => String(f.company) === companyId) : allFarms),
    [allFarms, companyId],
  );

  // Keep the company and farm valid for what the user can see.
  useEffect(() => {
    if (!allFarms.length) return;
    if (companyId && !companies.some((c) => c.id === companyId)) {
      setCompanyId('');
      return;
    }
    try { localStorage.setItem(COMPANY_KEY, companyId); } catch { /* ignore */ }
    setFarmId((current) => {
      if (current === ALL_FARMS && farms.length > 1) return current;
      if (farms.some((f) => String(f.id) === current)) return current;
      return farms.length > 1 ? ALL_FARMS : String(farms[0]?.id || '');
    });
  }, [allFarms.length, companies, companyId, farms]);

  const load = () => {
    if (!farmId) return Promise.resolve();
    const params = { year, ...(farmId === ALL_FARMS ? {} : { farm: farmId }), ...(companyId ? { company: companyId } : {}) };
    return api.get('fumigations/', { params }).then((r) => setRows(r.data));
  };

  useEffect(() => {
    if (!farmId) {
      setRows([]);
      return undefined;
    }
    try { localStorage.setItem(FARM_KEY, farmId); } catch { /* ignore */ }
    let stale = false;
    setLoading(true);
    setStatus(null);
    setWeek('');
    const params = { year, ...(farmId === ALL_FARMS ? {} : { farm: farmId }), ...(companyId ? { company: companyId } : {}) };
    api.get('fumigations/', { params })
      .then((r) => { if (!stale) setRows(r.data); })
      .catch((err) => { if (!stale) setStatus({ type: 'error', text: apiErrorText(err, 'No se pudieron cargar las fumigaciones.') }); })
      .finally(() => { if (!stale) setLoading(false); });
    return () => { stale = true; };
  }, [farmId, year, companyId]);

  // Back to "Todas las semanas" when the chosen week has no fumigations left (e.g. after deleting).
  useEffect(() => {
    if (week && !rows.some((r) => String(r.week) === week)) setWeek('');
  }, [rows, week]);

  const handleDelete = async (r) => {
    if (!window.confirm(`¿Eliminar el ciclo ${r.cycle} de ${r.farm_name}?`)) return;
    setStatus(null);
    try {
      await api.delete(`fumigations/${r.id}/`);
      await load();
    } catch (err) {
      setStatus({ type: 'error', text: apiErrorText(err, 'No se pudo eliminar la fumigación.') });
    }
  };

  const weekOptions = [...new Set(rows.map((r) => r.week))].sort((a, b) => a - b);
  const shown = week ? rows.filter((r) => String(r.week) === week) : rows;

  const allSelected = farmId === ALL_FARMS;
  const farmName = allSelected ? 'Todas las fincas' : farms.find((f) => String(f.id) === farmId)?.name || '';
  const companyName = companies.find((c) => c.id === companyId)?.name;
  const exportTitle = `FUMIGACIÓN AÉREA AÑO ${year}`;
  const exportSubtitle = (allSelected
    ? `TODAS LAS FINCAS${companyName ? ` · ${companyName.toUpperCase()}` : ''}`
    : `HDA ${farmName.toUpperCase()}`) + (week ? ` · SEMANA ${week}` : '');
  const exportFile = `Fumigacion_Aerea_${farmName.replace(/\s+/g, '_')}_${year}${week ? `_S${week}` : ''}`;
  const yearOptions = [thisYear + 1, thisYear, thisYear - 1, thisYear - 2];

  return (
    <div className="companies-root">
      <div className="page-topbar">
        <Link to="/production" className="topbar-btn">← Producción</Link>
        <div className="ps-topbar-actions">
          <div className="fum-export-menu-wrap" onBlur={(e) => { if (!e.currentTarget.contains(e.relatedTarget)) setExportMenu(false); }}>
            <button
              type="button" className="topbar-btn" onClick={() => setExportMenu((o) => !o)} disabled={!shown.length}
              aria-haspopup="menu" aria-expanded={exportMenu}
            >
              📤 Exportar ▾
            </button>
            {exportMenu && (
              <div className="fum-export-menu" role="menu">
                <button type="button" role="menuitem" onClick={() => { setExportMenu(false); setPreview(true); }}>
                  📊 Tabla <span>Excel, PDF o JPEG</span>
                </button>
                <button type="button" role="menuitem" onClick={() => { setExportMenu(false); setMessages({ rows: shown, title: `${exportTitle} · ${exportSubtitle}`, fileName: exportFile }); }}>
                  💬 Mensaje <span>Texto para WhatsApp</span>
                </button>
              </div>
            )}
          </div>
          <Link to="/fumigacion" className="topbar-btn topbar-btn-primary">+ Nueva fumigación</Link>
        </div>
      </div>
      <h1 className="companies-title">📋 Historial de Fumigación</h1>
      <p className="page-subtitle page-header-divided">Un ciclo de fumigación aérea por fila. Ábrelo para seguir editando, o elimínalo desde aquí.</p>

      <div className="history-filters">
        {companies.length > 1 && (
          <select className="history-select" value={companyId} onChange={(e) => setCompanyId(e.target.value)} aria-label="Empresa">
            <option value="">Todas las empresas</option>
            {companies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        )}
        <select className="history-select" value={farmId} onChange={(e) => setFarmId(e.target.value)} aria-label="Finca">
          {farms.length > 1 && <option value={ALL_FARMS}>Todas las fincas</option>}
          {farms.map((f) => <option key={f.id} value={f.id}>{f.name}</option>)}
        </select>
        <select className="history-select" value={year} onChange={(e) => setYear(Number(e.target.value))} aria-label="Año">
          {yearOptions.map((y) => <option key={y} value={y}>{y}</option>)}
        </select>
        <select className="history-select" value={week} onChange={(e) => setWeek(e.target.value)} aria-label="Semana" disabled={!weekOptions.length}>
          <option value="">Todas las semanas</option>
          {weekOptions.map((w) => <option key={w} value={w}>Semana {w}</option>)}
        </select>
      </div>

      {status && <div className={`companies-status companies-status-${status.type}`}>{status.text}</div>}

      <div className="companies-card fum-table-card">
        {loading ? (
          <div className="companies-empty">Cargando…</div>
        ) : shown.length === 0 ? (
          <div className="companies-empty">Aún no hay fumigaciones de {farmName} en {year}.</div>
        ) : (
          <table className="companies-table fum-table">
            <thead>
              <tr>
                {allSelected && <th>Finca</th>}
                <th>Ciclo</th>
                <th>Productos</th>
                <th>Fecha</th>
                <th>Tipo</th>
                <th>Volumen</th>
                <th>Ha fumigadas</th>
                <th>Compañía / Piloto</th>
                <th>Observaciones</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {shown.map((r) => (
                <tr key={r.id}>
                  {allSelected && <td>{r.farm_name}</td>}
                  <td><strong>{r.cycle}</strong></td>
                  <td>
                    <div className="fum-chips">
                      {r.products.map((p) => (
                        <span key={p.product} className="fum-chip">{p.product_name} <b>{fmtDoseUnit(p)}</b></span>
                      ))}
                    </div>
                  </td>
                  <td>
                    {/* Date, with its week and the days since the previous fumigation underneath;
                        hovering it spells each one out. */}
                    <div className="fum-date-cell" tabIndex={0}>
                      <div className="fum-date">{formatISODate(r.date)}</div>
                      <div className="fum-date-meta">
                        Sem. {r.week}{r.frequency == null ? '' : ` · ${r.frequency} días`}
                      </div>
                      <DateNote row={r} />
                    </div>
                  </td>
                  <td>{r.application_type_display}</td>
                  <td>{fmtVolume(r.volume)}</td>
                  <td>{r.fumigated_hectares == null ? '—' : fmtHa(r.fumigated_hectares)}</td>
                  <td>
                    {/* The company, with its pilot underneath (same look as the Fecha column). */}
                    <div className="fum-date">{r.fumigation_company_name}</div>
                    <div className="fum-date-meta">{r.pilot_name}</div>
                  </td>
                  <td className="fum-obs">{r.observations || '—'}</td>
                  <td>
                    <div className="row-actions">
                      <RowProperties item={r} title={`Ciclo ${r.cycle} · ${r.farm_name}`} />
                      <button
                        type="button" className="row-icon-btn" title="Exportar como mensaje" aria-label="Exportar como mensaje"
                        onClick={() => setMessages({
                          rows: [r],
                          title: `${r.farm_name} · Ciclo ${r.cycle} · ${formatISODate(r.date)}`,
                          fileName: `Fumigacion_${r.farm_name.replace(/\s+/g, '_')}_ciclo_${r.cycle}_${r.date}`,
                        })}
                      >
                        💬
                      </button>
                      <Link to={`/fumigacion/${r.id}`} className="row-icon-btn" title="Editar" aria-label="Editar">✏️</Link>
                      {canDelete && (
                        <button type="button" className="row-icon-btn" onClick={() => handleDelete(r)} title="Eliminar" aria-label="Eliminar">🗑️</button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {messages && (
        <MessageExport rows={messages.rows} title={messages.title} fileName={messages.fileName} onClose={() => setMessages(null)} />
      )}

      {preview && (
        <ExportPreviewModal
          title={`${exportTitle} · ${exportSubtitle}`}
          fileName={exportFile}
          onClose={() => setPreview(false)}
          onDownload={(format) => (format === 'excel'
            ? exportExcel(exportTitle, exportSubtitle, exportFile, shown, allSelected)
            : exportPDF(exportTitle, exportSubtitle, exportFile, shown, allSelected))}
        >
          {(() => {
            const { lead, productCols, body, width } = sheetModel(shown, allSelected);
            return (
              <table className="ps-table fum-print-table">
                <thead>
                  <tr><th className="ps-title" colSpan={width}>{exportTitle}</th></tr>
                  <tr><th className="fum-print-subtitle" colSpan={width}>{exportSubtitle}</th></tr>
                  <tr>
                    {lead.map((h) => <th key={h} className="ps-farm-head">{h}</th>)}
                    <th className="ps-farm-head" colSpan={productCols}>PRODUCTOS</th>
                    {AFTER_PRODUCTS.map((h) => <th key={h} className="ps-farm-head">{h}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {body.map((cells, r) => (
                    <tr key={shown[r].id}>
                      {cells.map((v, i) => {
                        const isProduct = i >= lead.length && i < lead.length + productCols;
                        return (
                          // eslint-disable-next-line react/no-array-index-key
                          <td key={i} className={isProduct || (allSelected && i === 0) ? 'fum-print-product' : 'fum-print-cell'}>
                            {i === lead.length - 1 ? <strong>{v}</strong> : v}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            );
          })()}
        </ExportPreviewModal>
      )}
    </div>
  );
}
